import type { JSX } from "preact";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { cn } from "./cn";
import { useFieldControl } from "./field";

export type SelectProps = Omit<
  JSX.IntrinsicElements["select"],
  "class" | "size"
> & {
  invalid?: boolean;
  class?: string;
};

const BASE =
  "block w-full appearance-none rounded-md border bg-bg pl-3 pr-9 py-2 text-sm text-fg " +
  "shadow-[var(--shadow-input)] outline-none transition " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const VALID =
  "border-border focus:border-accent focus:ring-2 focus:ring-accent/50";
const INVALID =
  "border-danger-border focus:border-danger-fg focus:ring-2 focus:ring-danger-fg/50";

export function Select({
  invalid,
  class: extraClass,
  children,
  id,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: SelectProps) {
  const field = useFieldControl(
    id as string | undefined,
    ariaDescribedBy as string | undefined,
  );
  return (
    <span class="relative inline-block w-full">
      <select
        {...rest}
        id={field.id}
        aria-describedby={field.describedBy}
        aria-invalid={invalid || undefined}
        class={cn(BASE, invalid ? INVALID : VALID, extraClass)}
      >
        {children}
      </select>
      <ChevronDownIcon
        aria-hidden
        class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle"
      />
    </span>
  );
}
